import { useCallback, useEffect, useRef, useState } from 'react';

// Copy-forked from portal-staff/src/lib/turnstile.ts (Common Catalogue model — each surface owns its copy).
/**
 * Loads the Cloudflare Turnstile script on demand and resolves with the
 * global API once it's available.
 *
 * Per Bot Protection Standard §3.4 — Turnstile + server-side siteverify. This module owns
 * the script loading; the component owns where the widget mounts and what the token is for.
 */
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let loadPromise: Promise<TurnstileApi> | null = null;

export function ensureTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${TURNSTILE_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('Turnstile script loaded but global missing'));
      });
      return;
    }
    const script = document.createElement('script');
    script.src = `${TURNSTILE_SRC}?render=explicit`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded but global missing'));
    });
    script.addEventListener('error', () => reject(new Error('Turnstile script load failed')));
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * Explicitly render one Turnstile widget into `containerRef` and track its token (1 October 2026).
 *
 * ⚠ WHY EXPLICIT. The Turnstile script is loaded with `?render=explicit`, and in that mode a
 * `<div class="cf-turnstile" data-sitekey=…>` is NEVER auto-rendered. The partner-application
 * steps relied on exactly that div, so even with a real site key no widget appeared, no token was
 * ever issued, and only the placeholder-token fallback made the form submittable. Rendering
 * through the API removes the dependence on script load order and on implicit scanning.
 *
 * `reset()` clears the token and re-challenges — call it after any failed submit, because a
 * Cloudflare token is single-use and the server has already spent it.
 */
export function useTurnstile(siteKey: string, theme: 'light' | 'dark' = 'light'): {
  containerRef: React.RefObject<HTMLDivElement>;
  token: string | null;
  loadError: boolean;
  reset: () => void;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!siteKey) { setLoadError(true); return; }
    let cancelled = false;
    ensureTurnstile().then((api) => {
      if (cancelled || !containerRef.current || widgetId.current) return;
      widgetId.current = api.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        callback: (t: string) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
      });
    }).catch(() => { if (!cancelled) setLoadError(true); });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* already gone */ }
        widgetId.current = null;
      }
    };
  }, [siteKey, theme]);

  const reset = useCallback(() => {
    setToken(null);
    try { if (widgetId.current) window.turnstile?.reset(widgetId.current); } catch { /* widget gone */ }
  }, []);

  return { containerRef, token, loadError, reset };
}
